import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import { baseUrl } from "../config";
import axiosConfig from "../utils/axiosConfig";
import { DashboardInfo, UserDetails } from "../utils/types";
import { useGoogleAuth } from "./GoogleAuthContext";

function rowMatchesSearch(
  title: string,
  section: string | null | undefined,
  value: string,
): boolean {
  const cleanTitle = title.toLowerCase();
  const cleanSection = (section ?? "").toLowerCase();

  if (value.includes(":")) {
    return cleanTitle.includes(value) || cleanSection.includes(value);
  }
  const keywords = value.trim().split(/\s+/);
  const combined = `${cleanTitle} ${cleanSection}`.trim();
  return keywords.every((word) => combined.includes(word));
}

function filterDashboardsBySearch(
  list: DashboardInfo[],
  value: string,
): DashboardInfo[] {
  return list
    .map((dashboard) => {
      const parentMatch = rowMatchesSearch(
        dashboard.title,
        dashboard.section,
        value,
      );
      const children = dashboard.child_dashboards ?? [];
      const matchingChildren = children.filter((child) =>
        rowMatchesSearch(
          child.title,
          child.section ?? dashboard.section,
          value,
        ),
      );

      if (parentMatch) {
        return dashboard;
      }
      if (matchingChildren.length > 0) {
        return { ...dashboard, child_dashboards: matchingChildren };
      }
      return null;
    })
    .filter((d): d is DashboardInfo => d !== null);
}

// Define the shape of the data context
interface DataContextType {
  searchInput: string;
  dashboardList: DashboardInfo[];
  primaryDashboardList: DashboardInfo[];
  handleSearchInputChange: (event: any) => void;
  clearSearch: () => void;
  loading: boolean;
}

// Create the data context
const DataContext = createContext<DataContextType | null>(null);

// Custom hook to access the data context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within an DataProvider");
  }
  return context;
};

// DataProvider component to wrap your application with
export const DataProvider = ({ children }: { children: any }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const isApiCallInProgress = React.useRef<boolean>(false);
  const [rowDashboardList, setRowDashboardList] = useState<DashboardInfo[]>([]);
  const [dashboardList, setDashboardList] = useState<DashboardInfo[]>([]);
  const [primaryDashboardList, setPrimaryDashboardList] = useState<
    DashboardInfo[]
  >([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const { user, setUser } = useGoogleAuth();

  const handleSearchInputChange = useCallback(
    (event: any) => {
      const value = event.target.value.toLowerCase();
      setSearchInput(value);

      if (value.trim() === "") {
        return setDashboardList(rowDashboardList);
      }

      setDashboardList(filterDashboardsBySearch(rowDashboardList, value));
    },
    [rowDashboardList],
  );

  const clearSearch = useCallback(() => {
    setSearchInput("");
    setDashboardList(rowDashboardList);
  }, [rowDashboardList]);

  useEffect(() => {
    if (!user) return;

    const checkAndUpdateUserRoles = async () => {
      try {
        const response = await axiosConfig({
          method: "get",
          url: `${baseUrl}/user-info/`,
        });

        if (response.status === 200) {
          const currentRoles = user.roles ?? [];
          const newRoles = response.data.roles ?? [];
          const rolesChanged =
            JSON.stringify(currentRoles) !== JSON.stringify(newRoles);
          const ceoAccessChanged =
            user.has_ceo_roles_access !==
            (response.data.has_ceo_roles_access ?? false);

          if (
            rolesChanged ||
            ceoAccessChanged ||
            user.has_ceo_roles_access === undefined
          ) {
            const updatedUserData: UserDetails = {
              ...user,
              ...response.data,
              has_ceo_roles_access: response.data.has_ceo_roles_access ?? false,
            };
            setUser(updatedUserData);
          }
        }
      } catch (error) {
        console.error("Error checking user roles:", error);
      }
    };

    checkAndUpdateUserRoles();
  }, [user, setUser]);

  const fetchDashboardList = React.useCallback(async () => {
    if (isApiCallInProgress.current) return;
    isApiCallInProgress.current = true;

    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/dashboard-list/`,
        });

        if (response.status === 200 && response.data) {
          setRowDashboardList(response.data.dashboard);
          setPrimaryDashboardList(response.data.primary_dashboard);
          setLoading(false);
          return response.data;
        } else {
          throw new Error("Failed to fetch the dashboard list.");
        }
      } catch (error) {
        console.error(error);
        throw new Error("An error occurred while fetching the dashboard list");
      } finally {
        setLoading(false);
        isApiCallInProgress.current = false;
      }
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardList();
    }
  }, [user?.roles, fetchDashboardList]);

  useEffect(() => {
    setDashboardList(rowDashboardList);
  }, [rowDashboardList]);

  const contextValue = useMemo(
    () => ({
      searchInput,
      handleSearchInputChange,
      clearSearch,
      dashboardList,
      primaryDashboardList,
      loading,
    }),
    [
      searchInput,
      handleSearchInputChange,
      clearSearch,
      dashboardList,
      primaryDashboardList,
      loading,
    ],
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};
